import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, State, StateContext, StateToken, Store } from '@ngxs/store';
import {
  AlertController,
  ModalController,
  NavController,
  ToastController
} from '@ionic/angular';
import { produce } from 'immer';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  pipe,
  UnaryFunction,
  lastValueFrom,
  from
} from 'rxjs';
import { StateReset } from 'ngxs-reset-plugin';
import { format } from 'date-fns';
import _ from 'lodash';
import { patch, StateOperator } from '@ngxs/store/operators';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

import {
  BaseMenuItem,
  LocationType,
  MacroGridView,
  MenuItemType,
  NormalizedEntities,
  NormalizedObject,
  NormalizedTicketItem,
  PaymentRequestType,
  PosLocation,
  Seat,
  StateObject,
  Ticket,
  TicketItem,
  TicketItemStatus,
  TicketSource,
  TicketStatus
} from 'src/app/models';
import { NormalizedTicket } from 'src/app/models/normalized-models/normalized-ticket.model';
import { DiningService, ModalService, TicketService } from 'src/app/services';
import { MenuHelper, NormalizrHelper } from 'src/app/helpers';
import { PageConstant } from 'src/app/constants';
import {
  LOG_ROCKET_CUSTOM_EVENTS,
  LogRocketProvider,
  SelfServiceProvider,
  TicketProvider
} from 'src/app/providers';
import {
  AddDinerToTicket,
  AddDinerToTicketSuccess,
  AddTaxExemption,
  AddTaxExemptionSuccess,
  AddTicketDiscount,
  AddTicketDiscountError,
  AddTicketItems,
  AddTicketMealPlan,
  AddTicketMealPlanSuccess,
  AssignGuestMealPlan,
  CancelServicesTicket,
  CancelTakeoutTicket,
  CancelTakeoutTicketSuccess,
  CancelTicket,
  ChangeTicketMeal,
  ChangeTicketMealSuccess,
  CleanSelectTicket,
  CloseTicket,
  CloseTicketError,
  CloseTicketSuccess,
  CloseTicketWsEvent,
  // CreateDraftTicket,
  CreateTicket,
  CreateTicketForSeatSuccess,
  CreateTicketSuccess,
  CreateTicketWsEvent,
  CreateTicketWsEventSuccess,
  FireIndividualOrder,
  FireListIndividualOrder,
  FireTicketAndClose,
  FireTicketAndCloseError,
  FireTicketSuccess,
  GetLastQuickServiceTicket,
  GetLastQuickServiceTicketSuccess,
  GetLocationTickets,
  GetTicketsSuccess,
  LoadTicketDetail,
  LoadTicketDetailSuccess,
  LockOrUnlockTicketSuccess,
  LockTicket,
  MealPlanPaymentError,
  PrintTicket,
  RemoveAllSskTicketItems,
  RemoveAllSskTicketItemsError,
  RemoveDinerFromTicket,
  RemoveDinerFromTicketError,
  RemoveDinerFromTicketSuccess,
  RemoveGuestOfDinerFromTicket,
  RemoveTaxExemption,
  RemoveTaxExemptionSuccess,
  RemoveTicketDiscount,
  RemoveTicketItem,
  RemoveTicketItemSuccess,
  RemoveTicketMealPlan,
  RemoveTicketSuccess,
  SelectBackQSTicket,
  SelectTicket,
  SetTicketCreatingStatus,
  SetTicketProcessing,
  SetupTakeoutOrder,
  SetupTakeoutOrderError,
  SetupTakeoutOrderSuccess,
  TemporarySeatMealChange,
  TemporarySeatMealChangeSuccess,
  TicketClosedWsEvent,
  TicketLockedChangedFromWS,
  ToggleTicketVisibility,
  UnlockTicket,
  UnselectTicket,
  UpdateFireTableSuccess,
  UpdateLocalTicketRelationships,
  UpdateTicket,
  UpdateTicketSuccess,
  VoidTicket
} from './tickets.action';
import { SEATS_STATE_TOKEN } from 'src/app/store/seats/seats.state';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';
import {
  ConfirmModal,
  ConfirmModalDismissData
} from 'src/app/modals/confirm/confirm.modal';
import {
  DINERS_STATE_TOKEN,
  DinersState
} from 'src/app/store/diners/diners.state';
import {
  TICKET_ITEMS_STATE_TOKEN,
  TicketItemsState
} from 'src/app/store/ticket-items/ticket-items.state';
import { TransactionsState } from 'src/app/store/transactions/transactions.state';
import { TicketHelper } from 'src/app/helpers/ticket.helper';
import { TAKEOUT_DELIVERY_STATE_TOKEN } from 'src/app/store/takeout-delivery/takeout-delivery.state';
import { GetTableSuccess } from 'src/app/store/tables/tables.action';
import {
  SelectDiner,
  UpdateChargeAccountBalance
} from 'src/app/store/diners/diners.action';
import {
  SelectNewSeat,
  SelectSeat,
  UnselectSeat,
  UpdateLocalSeat
} from 'src/app/store/seats/seats.action';
import {
  MakePayment,
  MakePaymentSuccess
} from 'src/app/store/payment/payment.action';
import {
  AddPickupDeliveryFeeToTicket,
  SelectTicketItem,
  UpdateTicketItemSuccess,
  VoidTicketItemSuccess
} from 'src/app/store/ticket-items/ticket-items.action';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import {
  OpenCashDrawer,
  SetLastTicketTransaction,
  StopIdle
} from 'src/app/store/app/app.action';
import {
  ExitTakeOutDelivery,
  GetTakeoutDeliveryTicketsSuccess,
  LockTakeoutTicket,
  UnlockTakeoutTicket
} from 'src/app/store/takeout-delivery/takeout-delivery.action';
import {
  SetSelectedMealId,
  ShowModifierGrid,
  UpdateMacroGridView
} from 'src/app/store/menu/menu.action';
import { UpdateSelectedMealId } from 'src/app/store/location/location.action';
import { HARDWARE_DEVICE_STATE_TOKEN } from 'src/app/store/hardware-device/hardware-device.model';
import { ExceptionError } from 'src/app/store/error/error.action';
import { MENU_STATE_TOKEN } from 'src/app/store/menu/menu.state.model';
import { DinerHelper } from 'src/app/helpers/diner.helper';
import { AppStateHelper } from 'src/app/store/app/app.state.helper';
import { LocationStateHelper } from 'src/app/store/location/location.state.helper';
import {
  CancelOrder,
  CancelOrderError
} from 'src/app/store/self-service/self-service.action';
import { TranslocoService } from '@ngneat/transloco';

export interface TicketsStateModel {
  tickets: NormalizedObject<NormalizedTicket>;
  selectedTicket: StateObject<string>;
  lastLocationTickets: StateObject<string[]>;
  isTicketVisible: boolean;
}

export const TICKETS_STATE_TOKEN = new StateToken<TicketsStateModel>('tickets');

@State({
  name: TICKETS_STATE_TOKEN,
  defaults: {
    tickets: {
      byId: {},
      allIds: []
    },
    selectedTicket: { data: null, isProcessing: false, isLoaded: false },
    lastLocationTickets: { data: [], isProcessing: false, isLoaded: false },
    isTicketVisible: false
  }
})
@Injectable()
export class TicketsState {
  constructor(
    private readonly store: Store,
    private readonly toastController: ToastController,
    private readonly modalController: ModalController,
    private readonly router: Router,
    private readonly ticketProvider: TicketProvider,
    private readonly navController: NavController,
    private readonly ticketService: TicketService,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly alertController: AlertController,
    private readonly modalService: ModalService,
    private readonly diningService: DiningService,
    private readonly sskProvider: SelfServiceProvider,
    private readonly translocoService: TranslocoService
  ) {}

  @Action(GetTakeoutDeliveryTicketsSuccess)
  getTakeoutDeliveryTicketsSuccess(
    { setState }: StateContext<TicketsStateModel>,
    { tickets }: GetTakeoutDeliveryTicketsSuccess
  ) {
    const normalizedTickets =
      NormalizrHelper.normalizeTickets(tickets).entities;

    setState(
      produce((draft) => {
        _.forEach(normalizedTickets.tickets, (normalizedTicket) => {
          draft.tickets.byId[normalizedTicket.device_ticket_uuid] = {
            ...draft.tickets.byId[normalizedTicket.device_ticket_uuid],
            ...normalizedTicket
          };
          draft.tickets.allIds = Object.keys(draft.tickets.byId);
        });
      })
    );
  }

  @Action(GetTableSuccess)
  getTablesSuccess(
    ctx: StateContext<TicketsStateModel>,
    { table }: GetTableSuccess
  ) {
    const _state = ctx.getState();
    const tickets = _.flatMap(table.seats, (seat) => (<Seat>seat).ticket)
      .filter((i) => !!i)
      .map((data) => {
        const ticket = _.cloneDeep(data);
        ticket.diner = { id: ticket.diner_id, first_name: '', last_name: '' };
        ticket.ticket_items = ticket.ticket_items.map((ti) =>
          this.addTicketItemLocalAttributes(ti, ticket.ticket_items)
        );
        if (
          ticket.device_ticket_uuid === _state.selectedTicket.data &&
          _state.tickets.byId &&
          _state.tickets.byId[_state.selectedTicket.data]
        ) {
          ticket.locked =
            _state.tickets.byId[_state.selectedTicket.data].locked;
          ticket.locked_by_operator_id =
            _state.tickets.byId[
              _state.selectedTicket.data
            ].locked_by_operator_id;
        }
        return ticket;
      });

    const entities = NormalizrHelper.normalizeTickets(tickets).entities;

    ctx.setState(
      produce((draft: TicketsStateModel) => {
        if (entities.tickets) {
          draft.tickets = {
            byId: { ...draft.tickets.byId, ...entities.tickets },
            allIds: _.union(draft.tickets.allIds, Object.keys(entities.tickets))
          };
        }
      })
    );

    //Select the Ticket diner if selecting the seat from Floor Plan)
    const state = ctx.getState();

    if (
      state.selectedTicket.data &&
      state.tickets.byId[state.selectedTicket.data]?.diner_id
    ) {
      this.selectDiner({
        ctx,
        ticketUuid: state.selectedTicket.data
        // ctx, state.selectedTicket.data
      });
    }

    if (state.selectedTicket.data) {
      const normalizedTicket = state.tickets.byId[state.selectedTicket.data];
      const ticketDate = normalizedTicket?.ticket_date;
      // fetch the menu by ticket meal id for clicking the seat directly in the seat diagram
      this.updateMenuForTicket(ctx, ticketDate, state.selectedTicket.data);
    }

    ctx.dispatch(new GetTicketsSuccess(entities));
  }

  @Action(LoadTicketDetail)
  loadTicketDetail(
    { setState, dispatch }: StateContext<TicketsStateModel>,
    { ticketUuid, selectTicket, loadMenu }: LoadTicketDetail
  ) {
    if (selectTicket) {
      setState(
        produce((draft) => {
          draft.selectedTicket = {
            data: ticketUuid,
            isProcessing: true,
            isLoaded: false
          };
        })
      );
    }

    return this.ticketService.getTicket(ticketUuid).pipe(
      tap((ticket) => {
        if (selectTicket) {
          setState(
            produce((draft) => {
              draft.selectedTicket = {
                data: ticketUuid,
                isProcessing: false,
                isLoaded: true
              };
            })
          );
        }

        if (ticket.status !== TicketStatus.CLOSED) {
          dispatch(new LoadTicketDetailSuccess(ticket, selectTicket, loadMenu));
        }
      })
    );
  }

  @Action(SelectTicket)
  selectTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticketId }: SelectTicket
  ) {
    const { tickets, selectedTicket } = ctx.getState();
    const selectedTicketId = selectedTicket.data;
    const operator = this.store.selectSnapshot(APP_STATE_TOKEN).operator;
    let currentTicket = tickets.byId && tickets.byId[selectedTicketId];

    if (
      selectedTicketId &&
      selectedTicketId !== ticketId && // only unlock the old ticket
      currentTicket &&
      currentTicket.locked &&
      currentTicket.locked_by_operator_id === operator.id
    ) {
      ctx.dispatch(new UnlockTicket(selectedTicketId));
    }

    ctx.setState(
      patch({
        selectedTicket: patch({
          data: ticketId
        })
      })
    );

    this.selectDiner({
      ctx,
      ticketUuid: ticketId
    });
    const actions: any[] = [new ShowModifierGrid(false)];

    if (ticketId) {
      currentTicket = tickets.byId && tickets.byId[ticketId];
      actions.push(new ToggleTicketVisibility(false));

      // fetch the meal base on the ticket meal id, it may cause a issue if the state.ticket.byId is empty. In UI, it's the case clicking on the seat directly.
      // Need to add more handler on the select seat.
      this.updateMenuForTicket(
        ctx,
        currentTicket?.ticket_date || this.formatDate(new Date()),
        ticketId,
        actions
      );
    }

    //Lock selected ticket if in Dining Room and not takeout delivery
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isTakeoutDelivery = this.store.selectSnapshot(
      TAKEOUT_DELIVERY_STATE_TOKEN
    ).enableTakeOutDelivery;
    if (ticketId && isDiningRoom && !isTakeoutDelivery) {
      actions.push(new LockTicket());
    }

    //Clear ticket item selection if selecting a new ticket
    const selectedTicketItemId = this.store.selectSnapshot(
      TICKET_ITEMS_STATE_TOKEN
    ).selectedTicketItem;
    if (selectedTicketItemId) {
      actions.push(new SelectTicketItem());
    }

    ctx.dispatch(actions);
  }

  @Action(CreateTicket)
  createTicket(
    { dispatch }: StateContext<TicketsStateModel>,
    { defaults, additionalData }: CreateTicket
  ) {
    return this.ticketService.createTicket(defaults).pipe(
      switchMap((ticket) => {
        if (additionalData) {
          const { diner_id, guest_of_diner_id, mealPlanId } = additionalData;

          if (diner_id || guest_of_diner_id) {
            return (
              diner_id
                ? this.ticketService.assignDinerToTicket(
                    ticket.device_ticket_uuid,
                    diner_id
                  )
                : this.ticketService.updateTicket(ticket.device_ticket_uuid, {
                    guest_of_diner_id
                  })
            ).pipe(
              switchMap((t) =>
                mealPlanId
                  ? this._assignMealPlanToTicket(
                      t.device_ticket_uuid,
                      mealPlanId
                    )
                  : of(t)
              )
            );
          }
        }

        return of(ticket);
      }),
      tap((ticket) => dispatch(new CreateTicketSuccess(ticket, true, true)))
    );
  }

  @Action([
    CreateTicketSuccess,
    AddTicketMealPlanSuccess,
    GetLastQuickServiceTicketSuccess,
    CreateTicketWsEventSuccess,
    LoadTicketDetailSuccess,
    VoidTicketItemSuccess
  ])
  updateLocalTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticket: t, selectTicket }: CreateTicketSuccess
  ) {
    const ticket = _.cloneDeep(t);
    const entities = NormalizrHelper.normalizeTickets([ticket]).entities;

    this.addTicketToState(ctx, ticket, selectTicket, entities);

    if (selectTicket && ticket?.id) {
      this.selectDiner({
        ctx,
        ticketUuid: ticket.device_ticket_uuid
      });
      const locationType =
        this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
      const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
      const hasSelectedSeat =
        !!this.store.selectSnapshot(SEATS_STATE_TOKEN).selectedSeat;

      const isTakeoutDelivery = this.store.selectSnapshot(
        TAKEOUT_DELIVERY_STATE_TOKEN
      ).enableTakeOutDelivery;

      if (isDiningRoom && hasSelectedSeat && !isTakeoutDelivery) {
        ctx.dispatch(new LockTicket());
      }
    }

    if (entities.ticket_items || entities.transactions) {
      ctx.dispatch(new UpdateLocalTicketRelationships(entities));
    }
  }

  @Action(UpdateTicket)
  updateTicket(
    { dispatch }: StateContext<TicketsStateModel>,
    { device_ticket_uuid, ticket, loadMenu }: UpdateTicket
  ) {
    return this.ticketService
      .updateTicket(device_ticket_uuid, {
        ...ticket
      })
      .pipe(
        tap((responseTicket) =>
          dispatch(new UpdateTicketSuccess(responseTicket, loadMenu))
        )
      );
  }

  @Action(CancelTicket)
  cancelTicket(
    { getState, dispatch, setState }: StateContext<TicketsStateModel>,
    { ticketUuid }: CancelTicket
  ) {
    const state = getState();
    const deviceTicketUuid = ticketUuid || state.selectedTicket.data;
    const ticketById = state.tickets.byId;
    const isTicketNotBeenDeleting =
      this.ticketService.deletingTickets.indexOf(deviceTicketUuid) === -1;
    const prevTicket = ticketById && ticketById[deviceTicketUuid];

    if (_.isNil(prevTicket?.id)) {
      return dispatch(new RemoveTicketSuccess(deviceTicketUuid, true));
    }

    if (isTicketNotBeenDeleting) {
      this._setTicketProcessing(setState, true);

      return this.ticketService.deleteTicket(deviceTicketUuid).pipe(
        catchError((error) => {
          if (
            error instanceof HttpErrorResponse &&
            error.status === HttpStatusCode.NotFound
          ) {
            this.alertController
              .create({
                header: this.translocoService.translate(
                  'errors.ticketNotFound'
                ),
                message: this.translocoService.translate(
                  'errors.ticketNotFoundMessage'
                ),
                buttons: [
                  {
                    text: this.translocoService.translate('common.ok'),
                    handler: () => {
                      this.store.dispatch(
                        new StateReset(
                          TicketsState,
                          TicketItemsState,
                          DinersState,
                          TransactionsState
                        )
                      );
                    }
                  }
                ]
              })
              .then((alert) => alert.present());
          }
          return EMPTY;
        }),
        tap(() => {
          const ticket = ticketById[deviceTicketUuid];
          this.logRocketProvider.track(
            LOG_ROCKET_CUSTOM_EVENTS.Ticket.Cancelled,
            { device_ticket_uuid: ticket?.device_ticket_uuid, id: ticket?.id }
          );

          dispatch(new RemoveTicketSuccess(deviceTicketUuid, true));
        }),
        finalize(() => this._setTicketProcessing(setState, false))
      );
    }
  }

  @Action(CancelTakeoutTicket)
  cancelTakeoutTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticket_uuid, operator_id }: CancelTakeoutTicket
  ) {
    return this.ticketService.cancelTicket(ticket_uuid, operator_id).pipe(
      tap(async (data) => {
        console.log('cancel ticket ', data);
        const isDiningRoom =
          this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type ===
          LocationType.DiningRoom;

        if (data.success) {
          this.navController.navigateRoot(
            PageConstant.TAKEOUT_DELIVERY_PAGE(isDiningRoom),
            {
              animated: true,
              animationDirection: 'back'
            }
          );
          ctx.dispatch(new CancelTakeoutTicketSuccess());
        } else {
          const alert = await this.alertController.create({
            header: this.translocoService.translate('messages.errorRefunding'),
            message: `${data.message}${this.translocoService.translate(
              'messages.contactSupport'
            )}`,
            cssClass: 'custom-alert-error',
            buttons: [this.translocoService.translate('common.ok')]
          });
          alert.present();
        }
      })
    );
  }

  @Action(VoidTicket)
  voidTicket(
    { getState, setState, dispatch }: StateContext<TicketsStateModel>,
    { payload, closed_from_source }: VoidTicket
  ) {
    const state = getState();
    const selectedTicketId = state.selectedTicket.data;
    if (this.ticketService.deletingTickets.indexOf(selectedTicketId) === -1) {
      this._setTicketProcessing(setState, true);
      return this.ticketService
        .voidTicket({
          void_operator_id:
            this.store.selectSnapshot(APP_STATE_TOKEN).operator.id,
          void_reason: payload.void_reason,
          ticket_uuid: selectedTicketId,
          approving_operator_id: payload.approving_operator_id,
          closed_from_source
        })
        .pipe(
          finalize(() => this._setTicketProcessing(setState, false)),
          tap(() => {
            const ticket = state.tickets.byId[selectedTicketId];
            if (ticket) {
              this.logRocketProvider.track(
                LOG_ROCKET_CUSTOM_EVENTS.Ticket.Voided,
                {
                  device_ticket_uuid: ticket.device_ticket_uuid,
                  id: ticket.id
                }
              );
            }
            dispatch(new RemoveTicketSuccess(selectedTicketId, true));
          })
        );
    }
  }

  @Action(FireIndividualOrder)
  fireIndividualOrder(
    ctx: StateContext<TicketsStateModel>,
    { ticketItemUuid, course, seat }: FireIndividualOrder
  ) {
    const state = ctx.getState();
    const operatorId = this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;
    const dispatchActions = (
      ticketItem: Partial<TicketItem>[],
      ticket_uuid: string
    ) => {
      ctx.dispatch([
        new UpdateTicketSuccess({
          can_be_cancelled: false,
          device_ticket_uuid: ticket_uuid
        }),
        new UpdateTicketItemSuccess(ticketItem)
      ]);
    };
    let data: Partial<{
      device_ticket_item_uuid: string;
      status: TicketItemStatus;
    }>[] = [];
    // Fire Ticket on the ticket component
    const selectedId = state.selectedTicket.data;
    if (!ticketItemUuid && !isFinite(course)) {
      // fire whole tickets
      return this.ticketService.fireTicket(selectedId, operatorId).pipe(
        tap((ticket_uuid) => {
          if (ticket_uuid) {
            const waitingTicketUuids = this.getWaitingTicketItemUuidArray(
              state.tickets.byId[selectedId]?.ticket_items || []
            );

            ctx.dispatch(
              new FireTicketSuccess(ticket_uuid, waitingTicketUuids)
            );
          }
        })
      );
    }
    // Fire Specific Item
    if (ticketItemUuid) {
      return this.ticketService
        .firePartialTickets(operatorId, ticketItemUuid)
        .pipe(
          tap((success) => {
            const ticketItems = this.store.selectSnapshot(
              TICKET_ITEMS_STATE_TOKEN
            ).ticketItems.byId;
            const selectedTicketItem = ticketItems[ticketItemUuid];
            // update ticket status
            if (success) {
              data = [
                ticketItemUuid,
                ..._.toArray(selectedTicketItem.ticket_items)
              ].map((device_ticket_item_uuid) => ({
                device_ticket_item_uuid,
                status: TicketItemStatus.FIRED
              }));
              dispatchActions(data, selectedId);
            }
          })
        );
    }
    // Fire ticket course on the ticket component
    const ticketUuid =
      (seat && seat.ticket && seat.ticket.device_ticket_uuid) || selectedId;
    return this.ticketService
      .firePartialTickets(operatorId, null, course, ticketUuid)
      .pipe(
        tap((success) => {
          // update ticket status
          if (success && state.tickets.byId && state.tickets.byId[ticketUuid]) {
            const ticketItems = this.store.selectSnapshot(
              TICKET_ITEMS_STATE_TOKEN
            ).ticketItems.byId;

            const waitingTicketUuids = this.getWaitingTicketItemUuidArray(
              state.tickets.byId[ticketUuid].ticket_items
            );

            data = waitingTicketUuids
              .map<Partial<TicketItem>>((device_ticket_item_uuid) => {
                const ticketItem = ticketItems[device_ticket_item_uuid];
                if (ticketItem && ticketItem.course === course) {
                  return Object.assign(
                    {},
                    { device_ticket_item_uuid, status: TicketItemStatus.FIRED }
                  );
                }
              })
              .filter(Boolean);
            dispatchActions(data, ticketUuid);
          }
        })
      );
  }

  @Action(FireListIndividualOrder)
  fireListIndividualOrder(
    ctx: StateContext<TicketsStateModel>,
    { listIndividualOrder, seat }: FireListIndividualOrder
  ) {
    return combineLatest(
      listIndividualOrder.map(({ ticketItemUuid, course }) =>
        ctx.dispatch(new FireIndividualOrder(ticketItemUuid, course, seat))
      )
    );
  }

  @Action(UpdateFireTableSuccess)
  updateFireTableSuccess(
    ctx: StateContext<TicketsStateModel>,
    { tableId, course }: UpdateFireTableSuccess
  ) {
    const state = ctx.getState();
    if (!state.tickets.byId) {
      return;
    }
    const ticketItems = this.store.selectSnapshot(TICKET_ITEMS_STATE_TOKEN)
      .ticketItems.byId;

    let data: Partial<TicketItem>[] = [];
    const tableTicketUuids = state.tickets.allIds.filter(
      (ticketUuid) => state.tickets.byId[ticketUuid]?.table_id === tableId
    );

    tableTicketUuids.forEach((ticketUuid) => {
      const waitingTicketUuids = this.getWaitingTicketItemUuidArray(
        state.tickets.byId[ticketUuid]?.ticket_items || []
      );

      data = data.concat(
        waitingTicketUuids
          .map<Partial<TicketItem>>((device_ticket_item_uuid) => {
            const ticketItem = _.has(ticketItems, device_ticket_item_uuid)
              ? ticketItems[device_ticket_item_uuid]
              : null;
            // DEF-4153 if(!course) always true if course is 0 causing all tickets marked as Fired
            if (
              _.isNil(course) ||
              (ticketItem && ticketItem.course === course)
            ) {
              return Object.assign(
                {},
                { device_ticket_item_uuid, status: TicketItemStatus.FIRED }
              );
            }
          })
          .filter(Boolean)
      );

      //Update can_be_cancelled on each ticket
      ctx.setState(
        produce((draft: TicketsStateModel) => {
          draft.tickets.byId[ticketUuid].can_be_cancelled = false;
        })
      );
    });

    ctx.dispatch(new UpdateTicketItemSuccess(data));
  }

  @Action(CloseTicket)
  closeTicket(
    { getState, dispatch, setState }: StateContext<TicketsStateModel>,
    payload: CloseTicket
  ) {
    const state = getState();
    const paymentTypes = payload.paymentTypes;
    const ticketUuid = payload.ticketUuid || state.selectedTicket.data;
    const ticket = state.tickets.byId[ticketUuid];
    const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
    const isSSK = location.type === LocationType.SelfServe;
    const actions: any[] = [];
    const doPrintTicket = TicketsState.doPrintTicket(
      location,
      ticket,
      paymentTypes
    );

    dispatch(new SetLastTicketTransaction(ticketUuid));

    if (doPrintTicket) {
      actions.push(new PrintTicket(ticketUuid, false));
    }

    actions.push(new RemoveTicketSuccess(ticketUuid, true));

    //If ticket is closed on self serve kiosk screen, dispatch close ticket success action
    if (isSSK) {
      actions.push(new CloseTicketSuccess());
    }

    if (ticket?.status === TicketStatus.CLOSED) {
      return dispatch(actions);
    }

    this._setTicketProcessing(setState, true);

    return this.ticketService
      .closeTicket({
        device_ticket_uuid: ticketUuid
      })
      .pipe(
        finalize(() => this._setTicketProcessing(setState, false)),
        tap(() => {
          this.logRocketProvider.track(LOG_ROCKET_CUSTOM_EVENTS.Ticket.Closed, {
            device_ticket_uuid: ticket?.device_ticket_uuid,
            id: ticket?.id
          });
          dispatch(actions);
        }),
        catchError((httpError: HttpErrorResponse) => {
          const error = httpError.error;
          if (!error) {
            dispatch(
              new CloseTicketError(
                this.translocoService.translate('messages.problemClosingTicket')
              )
            );
            return EMPTY;
          }

          const { message, status } = error;
          switch (status) {
            case 'already_closed':
              dispatch(actions);
              return EMPTY;
            case 'not_paid':
              // If the app cannot close the ticket due to a not_paid error. It means there are balances remaining.
              // Therefore, refetch the ticket to reflect the real balances.
              return this.ticketService.getTicket(ticketUuid).pipe(
                tap((response) => {
                  dispatch([
                    new LoadTicketDetailSuccess(response),
                    new CloseTicketError(message)
                  ]);
                }),
                catchError(() =>
                  dispatch(
                    new CloseTicketError(
                      this.translocoService.translate(
                        'messages.problemClosingTicket'
                      )
                    )
                  )
                )
              );
            default:
              dispatch(
                message?.includes('Insufficient mealplan')
                  ? new MealPlanPaymentError(ticketUuid, message)
                  : new CloseTicketError(
                      this.translocoService.translate(
                        'messages.problemClosingTicket'
                      )
                    )
              );
              return EMPTY;
          }
        })
      );
  }

  @Action(MealPlanPaymentError)
  MealPlanPaymentError(
    ctx: StateContext<TicketsStateModel>,
    payload: MealPlanPaymentError
  ) {
    this.modalService.showModal<ConfirmModalDismissData>(
      ConfirmModal,
      {
        header: this.translocoService.translate(
          'messages.mealPlanPaymentError'
        ),
        subTitle: payload.errorMessage,
        redBtnHidden: true,
        grayBtnLabel: this.translocoService.translate('messages.removeMealPlan')
      },
      {
        cssClass: ['pos-confirm-modal', 'dynamic-height-modal'],
        backdropDismiss: false
      },
      () => ctx.dispatch(new RemoveTicketMealPlan(payload.ticketUuid))
    );
  }

  @Action(AddTicketMealPlan)
  addTicketMealPlan(
    ctx: StateContext<TicketsStateModel>,
    { payload }: AddTicketMealPlan
  ) {
    return this._assignMealPlanToTicket(
      payload.ticketUuid,
      payload.mealPlanId
    ).pipe(
      tap((ticket) =>
        ctx.dispatch([
          new AddTicketMealPlanSuccess(ticket),
          new UpdateTicketSuccess(_.cloneDeep(ticket))
        ])
      )
    );
  }

  @Action(RemoveTicketMealPlan)
  removeTicketMealPlan(
    ctx: StateContext<TicketsStateModel>,
    { ticketUuid }: RemoveTicketMealPlan
  ) {
    return from(
      this.modalService.showLoadingModal(
        this.translocoService.translate('messages.removingMealPlan')
      )
    ).pipe(
      switchMap((loadingModal) =>
        this.ticketService.removeMealPlan(ticketUuid).pipe(
          catchError(() => {
            void this.modalService.closeLoadingModal(loadingModal.id);
            return EMPTY;
          }),
          tap((ticket) => {
            void this.modalService.closeLoadingModal(loadingModal.id);
            const actions = [
              new UpdateTicketItemSuccess(ticket.ticket_items),
              new UpdateTicketSuccess({
                ...ticket,
                meal_plan: null
              })
            ];
            ctx.dispatch(actions);
          })
        )
      )
    );
  }

  @Action(AddTicketDiscount)
  addTicketDiscount(
    ctx: StateContext<TicketsStateModel>,
    { discount_id }: AddTicketDiscount
  ) {
    const state = ctx.getState();
    const operator = this.store.selectSnapshot(APP_STATE_TOKEN).operator;
    return this.ticketService
      .addTicketDiscount(state.selectedTicket.data, operator.id, discount_id)
      .pipe(
        switchMap((ticket) => {
          this.modalController.dismiss();
          if (!ticket) {
            ctx.dispatch(new ExceptionError('Ticket not found'));
            return;
          }

          const actions = [
            new UpdateTicketSuccess(ticket),
            new UpdateTicketItemSuccess(ticket.ticket_items)
          ];
          return ctx.dispatch(actions);
        }),
        catchError((err) => ctx.dispatch(new AddTicketDiscountError(err)))
      );
  }

  @Action(RemoveTicketDiscount)
  removeTicketDiscount(
    ctx: StateContext<TicketsStateModel>,
    { discount_id }: RemoveTicketDiscount
  ) {
    const state = ctx.getState();
    return this.ticketService
      .removeTicketDiscount(state.selectedTicket.data, discount_id)
      .pipe(
        tap((ticket) => {
          ctx.dispatch(
            new UpdateTicketSuccess({
              ...ticket,
              applied_discount_id: null,
              total_discounts: 0
            })
          );
          ctx.dispatch(new UpdateTicketItemSuccess(ticket.ticket_items));
        })
      );
  }

  @Action(AddTicketMealPlanSuccess)
  autoAddMealPlanItems(
    { getState }: StateContext<TicketsStateModel>,
    { ticket }: AddTicketMealPlanSuccess
  ) {
    const { currentMealId, location } =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN);
    const autoAddItems: number[] =
      LocationStateHelper.getAutoAddProductsForMealPlan(
        currentMealId,
        location
      );
    const isSelfServicePaymentPage = this.router.url.includes(
      PageConstant.SELF_SERVICE_PAYMENT_PAGE
    );

    const ticketDateMealId =
      ticket && `${ticket.ticket_date}_${ticket.meal_id}`;

    const shouldProcessAutoAddItems =
      ticketDateMealId &&
      autoAddItems &&
      autoAddItems?.length > 0 &&
      !isSelfServicePaymentPage;

    if (shouldProcessAutoAddItems) {
      const { byId: ticketsById, allIds: ticketAllIds } = getState().tickets;
      const device_ticket_uuid = ticket.device_ticket_uuid;
      if (ticketAllIds.includes(device_ticket_uuid)) {
        const { diners } = this.store.selectSnapshot(DINERS_STATE_TOKEN);
        const diner = DinerHelper.getTicketDiner(
          diners.byId,
          ticketsById[device_ticket_uuid].diner_id
        );
        const autoAddOnZeroMpBalance = location.auto_add_on_zero_mp_balance;
        const dinerMealPlanBalance = diner.meal_plan.balance;

        const shouldAutoAddItems =
          autoAddOnZeroMpBalance || dinerMealPlanBalance > 0;

        if (shouldAutoAddItems) {
          const { selected_macro_grid, menu, menu_tally } =
            this.store.selectSnapshot(MENU_STATE_TOKEN);
          const locationType =
            this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
          const isServicesLocation = locationType === LocationType.Services;

          const menuItems = MenuHelper.getMenuItems(
            selected_macro_grid,
            menu,
            menu_tally,
            diner,
            isServicesLocation,
            ticketDateMealId
          );

          const autoItems = (menuItems || [])?.filter(
            (x) =>
              x &&
              x.type === MenuItemType.Product &&
              autoAddItems.includes(x.id)
          );

          autoItems.forEach((item) => {
            const baseMenuItem: BaseMenuItem = {
              ...item,
              quantity: 1
            };

            this.ticketProvider.ticketItemAddingHandler({
              menuItem: baseMenuItem,
              device_ticket_item_uuid: ticket.device_ticket_uuid
            });
          });
        }
      }
    }
  }

  @Action(RemoveTicketSuccess)
  removeTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    payload: RemoveTicketSuccess
  ) {
    this.removeTicketSuccessHandler(ctx, payload);
  }

  @Action(AddDinerToTicket)
  addDinerToTicket(
    { dispatch, getState }: StateContext<TicketsStateModel>,
    { payload }: AddDinerToTicket
  ) {
    const { byId, allIds } = getState().tickets;
    const { device_ticket_uuid, diner, forceApplyMealPlan } = payload;
    const diner_id = diner.id;
    if (!device_ticket_uuid || allIds.includes(device_ticket_uuid)) {
      const { type: locationType } =
        this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
      const isDiningRoom = locationType === LocationType.DiningRoom;
      const selectedTicket = byId[device_ticket_uuid];
      const isDraftTicket = !selectedTicket?.status;
      const isNotPOSTakeOutOrder =
        selectedTicket?.order_source === TicketSource.POS;
      const seatId =
        isDiningRoom && isNotPOSTakeOutOrder
          ? this.store.selectSnapshot(SEATS_STATE_TOKEN).seats.byId[
              selectedTicket?.seat_id
            ].id
          : null;

      const assignDiner = (ticketUuid: string) =>
        this.ticketService.assignDinerToTicket(ticketUuid, diner_id).pipe(
          tap((res) => {
            dispatch([
              new AddDinerToTicketSuccess({
                device_ticket_uuid: ticketUuid,
                diner,
                addMealPlan: true,
                getDinerDetail: true,
                forceApplyMealPlan: !!forceApplyMealPlan,
                seatId
              }),
              new LoadTicketDetailSuccess(res)
            ]);
          })
        );
      const assignDinerAction = (ticketUuid: string) =>
        selectedTicket.diner_id
          ? dispatch(
              new RemoveDinerFromTicket(selectedTicket.device_ticket_uuid)
            ).pipe(switchMap(() => assignDiner(ticketUuid)))
          : assignDiner(ticketUuid);

      if (isDraftTicket) {
        dispatch(new CreateTicket())
          .pipe(switchMap(() => assignDiner(getState().selectedTicket.data)))
          .subscribe();
      } else {
        return assignDinerAction(payload.device_ticket_uuid);
      }
    }
  }

  @Action(AddDinerToTicketSuccess)
  addDinerToTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    { payload }: AddDinerToTicketSuccess
  ) {
    // const { diner, addMealPlan, getDinerDetail, forceApplyMealPlan } = payload;
    const { device_ticket_uuid, diner } = payload;
    const {
      id: diner_id,
      name: diner_name,
      first_name: diner_first_name,
      last_name: diner_last_name
    } = diner;
    ctx.setState(
      produce((draft) => {
        const { byId, allIds } = draft.tickets;
        if (allIds.includes(device_ticket_uuid)) {
          byId[device_ticket_uuid].diner = diner_id;
          byId[device_ticket_uuid].diner_id = diner_id;
          byId[device_ticket_uuid].diner_name =
            diner_name || `${diner_first_name} ${diner_last_name}`;
        }
      })
    );

    this.selectDiner({
      ctx,
      ticketUuid: device_ticket_uuid,
      addMealPlan: payload.addMealPlan,
      forceApplyMealPlan: payload.forceApplyMealPlan
    });
    this.updateDinerToLocalSeat(ctx, device_ticket_uuid);
  }

  @Action([RemoveDinerFromTicket, RemoveGuestOfDinerFromTicket])
  removeDinerFromTicket(
    { getState, dispatch }: StateContext<TicketsStateModel>,
    action: RemoveDinerFromTicket | RemoveGuestOfDinerFromTicket
  ) {
    const selectedTicketId = action.device_ticket_uuid;
    const isGuestOfDiner = action instanceof RemoveGuestOfDinerFromTicket;
    const { allIds, byId } = getState().tickets;

    if (allIds.includes(selectedTicketId)) {
      const selectedTicket = byId[selectedTicketId];

      if (!selectedTicket.diner_id && !selectedTicket.guest_of_diner_id) {
        return;
      }

      if (selectedTicket.status === 'partial_paid') {
        const dinerType = isGuestOfDiner
          ? this.translocoService.translate('messages.guestOfDiner')
          : this.translocoService.translate('messages.diner');
        return dispatch(
          new RemoveDinerFromTicketError(
            this.translocoService.translate(
              'messages.cannotRemoveDinerPartialPaid',
              { type: dinerType }
            )
          )
        );
      }

      // const ticket = state.tickets.byId[selectedTicketId];
      // If the ticket is assigned to a diner, remove the diner.
      // If the ticket is assigned to a guest of a diner, remove the guest of the diner
      const removeDinerService = isGuestOfDiner
        ? this.ticketService.updateTicket(selectedTicketId, {
            guest_of_diner_id: null
          })
        : this.ticketService.clearDinerAssignment(selectedTicketId);
      const removeDiner$ = removeDinerService.pipe(
        switchMap((res) => dispatch(new RemoveDinerFromTicketSuccess(res))),
        catchError((error: HttpErrorResponse) =>
          dispatch(new RemoveDinerFromTicketError(JSON.stringify(error.error)))
        )
      );

      // If the ticket has a meal plan, remove the meal plan first and then run the remove diner service to avoid meal plan errors
      const hasMealPlan = !!selectedTicket.meal_plan;
      const removeMealPlan = () =>
        dispatch(new RemoveTicketMealPlan(selectedTicketId)).pipe(
          mergeMap(() => removeDiner$)
        );
      return hasMealPlan ? removeMealPlan() : removeDiner$;
    }
  }

  @Action(RemoveDinerFromTicketSuccess)
  removeDinerFromTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    payload: RemoveDinerFromTicketSuccess
  ) {
    const state = ctx.getState();
    const selectedTicketId = state.selectedTicket.data;
    const ticket = state.tickets.byId[selectedTicketId];

    this.logRocketProvider.track(LOG_ROCKET_CUSTOM_EVENTS.Diner.Unassigned, {
      device_ticket_uuid: ticket.device_ticket_uuid,
      diner_name: ticket.diner_name,
      diner_id: ticket.diner_id
    });

    ctx.setState(
      produce((draft: TicketsStateModel) => {
        const selectedDraftTickedId = draft.selectedTicket.data;
        if (draft.tickets.byId[selectedDraftTickedId]) {
          draft.tickets.byId[selectedDraftTickedId].diner = null;
          draft.tickets.byId[selectedDraftTickedId].diner_id = null;
          draft.tickets.byId[selectedDraftTickedId].diner_name = null;
        }
      })
    );

    this.selectDiner({
      ctx,
      ticketUuid: selectedTicketId
    });
    this.updateDinerToLocalSeat(ctx, selectedTicketId);
    ctx.dispatch(new LoadTicketDetailSuccess(payload.ticket));
  }

  @Action(MakePayment)
  updateTicketWhenPaymentMade(
    ctx: StateContext<TicketsStateModel>,
    { payment_request, ticket }: MakePayment
  ) {
    const change_due = payment_request?.change_due,
      device_ticket_uuid = ticket?.device_ticket_uuid;

    if (!device_ticket_uuid || !change_due) {
      return;
    }

    // update it to store and send to external screen
    TicketsState.updateTicketSuccess(ctx, {
      device_ticket_uuid,
      change_due
    });
  }

  @Action(UpdateTicketSuccess)
  updateTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    { ticket }: UpdateTicketSuccess
  ) {
    TicketsState.updateTicketSuccess(ctx, ticket);
  }

  @Action(AddTicketItems)
  addTicketItems(
    ctx: StateContext<TicketsStateModel>,
    { updateTicket, ticketItemsUuids }: AddTicketItems
  ) {
    const normalizeTickets = NormalizrHelper.normalizeTickets([updateTicket])
      .entities.tickets;
    const deviceTicketUuid = updateTicket.device_ticket_uuid;

    ctx.setState(
      produce((draft: TicketsStateModel) => {
        if (normalizeTickets) {
          draft.tickets.byId[deviceTicketUuid] = _.merge(
            {},
            draft.tickets.byId[deviceTicketUuid],
            normalizeTickets[deviceTicketUuid]
          );
        }
        draft.tickets.byId[deviceTicketUuid].ticket_items =
          ticketItemsUuids || [];
      })
    );

    const platform = this.store.selectSnapshot(
      HARDWARE_DEVICE_STATE_TOKEN
    ).platform;
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isKiosk: boolean = locationType === LocationType.SelfServe;
    const isSmallScreen = window.innerWidth < 1199;
    if (!isKiosk && platform !== 'web' && isSmallScreen) {
      this.toastController
        .create({
          color: 'primary',
          duration: 2000,
          message: this.translocoService.translate('messages.menuItemAdded'),
          position: 'top'
        })
        .then((toast) => toast.present());
    }
  }

  // Removes device_ticket_item_uuid from ticket items array in a normalized ticket
  @Action(RemoveTicketItem)
  removeTicketItem(
    ctx: StateContext<TicketsStateModel>,
    { ticketItemUuids }: RemoveTicketItem
  ) {
    ctx.setState(
      produce((draft: TicketsStateModel) => {
        _.remove(
          draft.tickets.byId[draft.selectedTicket.data]?.ticket_items || [],
          (item) => ticketItemUuids.includes(item)
        );
      })
    );
  }

  @Action(RemoveTicketItemSuccess)
  removeTicketItemSuccess(
    ctx: StateContext<TicketsStateModel>,
    payload: RemoveTicketItemSuccess
  ) {
    const ticket = _.cloneDeep(payload.ticket);
    const normalizeTickets = NormalizrHelper.normalizeTickets([ticket]).entities
      .tickets;
    ctx.setState(
      produce((draft: TicketsStateModel) => {
        ticket.ticket_items = ticket.ticket_items.filter(
          (ti) => !ti.parent_uuid
        );
        const ticketUuid =
          ticket.device_ticket_uuid || draft.selectedTicket.data;
        if (normalizeTickets) {
          draft.tickets.byId[ticketUuid] = _.merge(
            {},
            draft.tickets.byId[ticketUuid],
            normalizeTickets[ticketUuid]
          );
          draft.tickets.byId[ticketUuid].ticket_items =
            normalizeTickets[ticketUuid].ticket_items;
        }
      })
    );
  }

  @Action(MakePaymentSuccess)
  makePaymentSuccess(
    { setState, dispatch, getState }: StateContext<TicketsStateModel>,
    { device_ticket_uuid, payment }: MakePaymentSuccess
  ) {
    const {
      type: locationType,
      print_receipt_for_cash_payment,
      auto_close_on_full_payment,
      print_receipt_for_non_cash
    } = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
    const { new_balance, transaction, ticket_status, ticket } = payment;
    const transactionType = transaction.type;
    const isFullPayment = +new_balance === 0;
    const isCashPayment = transactionType === PaymentRequestType.CASH;
    const isSSK = locationType === LocationType.SelfServe;
    const printTicketOnFullPayment =
      isFullPayment &&
      auto_close_on_full_payment &&
      !isSSK &&
      (isCashPayment
        ? print_receipt_for_cash_payment
        : print_receipt_for_non_cash);

    const isTakeoutDeliveryTicket =
      ticket?.order_source !== TicketSource.POS ||
      ticket_status === TicketStatus.TAKEOUT_ORDER_IN_PROGRESS;
    const ticketIsAutoClosed: boolean =
      auto_close_on_full_payment &&
      (ticket_status === TicketStatus.CLOSED ||
        (isTakeoutDeliveryTicket && isFullPayment));

    if (printTicketOnFullPayment) {
      dispatch(new PrintTicket(device_ticket_uuid, false));
    }

    if (isCashPayment) {
      dispatch(new OpenCashDrawer());
    }

    if (ticketIsAutoClosed) {
      dispatch(new SetLastTicketTransaction(device_ticket_uuid));
    }

    const { allIds: allTicketIds, byId: ticketsById } = getState().tickets;

    if (!allTicketIds.includes(device_ticket_uuid)) {
      return;
    }

    const { diner_id } = ticketsById[device_ticket_uuid];

    setState(
      produce((draft) => {
        const draftTicket = draft.tickets.byId[device_ticket_uuid];
        draftTicket.status = ticket_status;
        draftTicket.outstanding_balance = parseFloat(new_balance);
        draftTicket.transactions = _.compact(
          _.concat(draftTicket.transactions, transaction.id)
        );
      })
    );

    // If the payment is made on SSK:
    // 1. User need to manually close the ticket
    // 2. We don't need to update the charge account balance due to the diners state has been reset (Ticket was removed)
    // 3. We don't have cash payment method.
    if (isSSK) {
      return;
    }

    //Auto Close Ticket
    if (ticketIsAutoClosed) {
      dispatch(new RemoveTicketSuccess(device_ticket_uuid, true));
    } else {
      /** If the ticket is auto closed when fully payment we don't need to update the charge account balance due to the diners state has been reset (Ticket was removed) */
      if (transactionType === PaymentRequestType.CHARGE_ACCOUNT && diner_id) {
        dispatch(new UpdateChargeAccountBalance(diner_id, transaction));
      }
    }
  }

  // This action should not be called alone, must belong to clean the selected ticket and recall another action immediately
  @Action(CleanSelectTicket)
  cleanSelectTicket(ctx: StateContext<TicketsStateModel>) {
    ctx.setState(
      produce((draft: TicketsStateModel) => {
        draft.selectedTicket = {
          data: null,
          isProcessing: false,
          isLoaded: false
        };
      })
    );
  }

  @Action(GetLastQuickServiceTicket)
  getLastQuickServiceTicket({ dispatch }: StateContext<TicketsStateModel>) {
    return this.ticketService.getTickets().pipe(
      tap((tickets) => {
        const deviceId = this.store.selectSnapshot(APP_STATE_TOKEN).device.id;
        const lastDeviceTicket = _.findLast(
          tickets,
          ({ device_id }) => device_id === deviceId
        );
        const deviceTicketUuid = lastDeviceTicket?.device_ticket_uuid;

        if (deviceTicketUuid) {
          dispatch(new LoadTicketDetail(deviceTicketUuid, true, true));
          return;
          // this.ticketService
          //   .getTicket(uuid)
          //   .pipe(
          //     map((ticket) => ({
          //       ...ticket,
          //       ticket_items: ticket.ticket_items.map((ti) =>
          //         this.addTicketItemLocalAttributes(ti, ticket.ticket_items)
          //       )
          //     }))
          //   )
          //   .subscribe((ticket) =>
          //     dispatch(new GetLastQuickServiceTicketSuccess(ticket, true))
          //   );
        } else {
          const ticket = TicketHelper.initializeTicket({
            device_ticket_uuid:
              this.ticketService.generateTicketOrTicketItemUuid('TICKET')
          });
          dispatch(new GetLastQuickServiceTicketSuccess(ticket, true));
        }
        dispatch(new UpdateMacroGridView(MacroGridView.MAIN));
      })
    );
  }

  @Action(GetLocationTickets)
  getLocationTickets({ patchState }: StateContext<TicketsStateModel>) {
    return this.ticketService.getTickets().pipe(
      tap((tickets) => {
        const device_id = this.store.selectSnapshot(APP_STATE_TOKEN).device.id;
        patchState({
          lastLocationTickets: {
            data: tickets.reduce<string[]>((prev, ticket) => {
              if (ticket.device_id === device_id) {
                prev.push(ticket.device_ticket_uuid);
              }

              return prev;
            }, []),
            isProcessing: false,
            isLoaded: true
          }
        });
      }),
      catchError((e: HttpErrorResponse) => {
        this.logRocketProvider.log(e.message);
        return EMPTY;
      })
    );
  }

  @Action(SelectBackQSTicket)
  selectBackQSTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticketUuid }: SelectBackQSTicket
  ) {
    if (ticketUuid) {
      // get ticket object from state
      ctx.dispatch(new SelectTicket(ticketUuid));
    } else {
      const ticket = TicketHelper.initializeTicket({
        device_ticket_uuid:
          this.ticketService.generateTicketOrTicketItemUuid('TICKET')
      });
      ctx.dispatch(new GetLastQuickServiceTicketSuccess(ticket, true));
    }
    ctx.dispatch(new UpdateMacroGridView(MacroGridView.MAIN));
  }

  @Action(ToggleTicketVisibility)
  toggleTicketVisibility(
    ctx: StateContext<TicketsStateModel>,
    { isVisible }: ToggleTicketVisibility
  ) {
    const state = ctx.getState();
    ctx.patchState({
      isTicketVisible:
        isVisible !== undefined ? isVisible : !state.isTicketVisible
    });
  }

  //Dispatched if create Ticket update happens on the same table
  @Action(CreateTicketWsEvent)
  createTicketWSEvent(
    ctx: StateContext<TicketsStateModel>,
    { wsData }: CreateTicketWsEvent
  ) {
    const ticketById = ctx.getState().tickets.byId;
    const prevTicket = ticketById && ticketById[wsData.device_ticket_uuid];
    return this.ticketService.getTicket(wsData.device_ticket_uuid).pipe(
      filter((ticket) => !!ticket),
      map((ticket) =>
        !!prevTicket
          ? {
              ...ticket,
              locked: prevTicket.locked,
              locked_by_operator_id: prevTicket.locked_by_operator_id
            }
          : ticket
      ),
      tap((ticket) =>
        ctx.dispatch([
          new CreateTicketWsEventSuccess(ticket),
          new UpdateLocalSeat({
            id: wsData.seat_id,
            ticket: ticket.device_ticket_uuid,
            device_ticket_uuid: ticket.device_ticket_uuid,
            ticket_created_at: ticket.created_at,
            ticket_status: ticket.status,
            diner_id: ticket.diner_id
          })
        ])
      )
    );
  }

  //Dispatched if create Ticket update happens on the same table
  @Action(CloseTicketWsEvent)
  closeTicketWSEvent(
    ctx: StateContext<TicketsStateModel>,
    { wsData }: CloseTicketWsEvent
  ) {
    const state = ctx.getState();

    if (
      _.isNil(
        state.tickets.byId && state.tickets.byId[wsData.device_ticket_uuid]
      )
    ) {
      return;
    }

    ctx.setState(
      produce((draft: TicketsStateModel) => {
        _.remove(
          draft.tickets.allIds,
          (id) => id === wsData.device_ticket_uuid
        );
        delete draft.tickets.byId[wsData.device_ticket_uuid];
      })
    );
  }

  @Action(PrintTicket)
  printTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticketUuid, open_cash_drawer }: PrintTicket
  ) {
    const submittedTicketUuid =
      ticketUuid || ctx.getState().selectedTicket.data;

    const { defaultPrinter, printers, payment_location } =
      this.store.selectSnapshot(APP_STATE_TOKEN);
    const selectedPrinter: number = AppStateHelper.getDefaultPrinter(
      defaultPrinter,
      printers,
      payment_location?.default_printer_id
    );

    if (selectedPrinter && submittedTicketUuid) {
      return this.ticketService.printTicket(
        submittedTicketUuid,
        selectedPrinter,
        open_cash_drawer
      );
    }
  }

  //Call the API to lock the current selected ticket
  @Action(LockTicket)
  lockTicket({ getState, dispatch }: StateContext<TicketsStateModel>) {
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isTakeoutDelivery = this.store.selectSnapshot(
      TAKEOUT_DELIVERY_STATE_TOKEN
    ).enableTakeOutDelivery;

    if (!isDiningRoom || isTakeoutDelivery) {
      return;
    }

    const { selectedTicket, tickets } = getState();
    const selectedTicketId = selectedTicket.data;
    const operatorId = this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;
    const ticket = tickets.byId && tickets.byId[selectedTicketId];

    if (ticket.locked && ticket.locked_by_operator_id === operatorId) {
      return;
    }

    dispatch([
      new LockOrUnlockTicketSuccess(selectedTicketId, true, operatorId),
      new UpdateLocalSeat({
        id: ticket?.seat_id,
        ticket_locked: true,
        ticket_locked_by_operator_id: operatorId
      })
    ]);

    return this.ticketService.lockTicket(selectedTicketId, operatorId);
  }

  //Call the API to unlock the current selected ticket
  @Action(UnlockTicket)
  unlockTicket(
    ctx: StateContext<TicketsStateModel>,
    { device_ticket_uuid }: UnlockTicket
  ) {
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const ticket = ctx.getState().tickets.byId[device_ticket_uuid];

    if (isDiningRoom && ticket.locked) {
      ctx.dispatch([
        new LockOrUnlockTicketSuccess(device_ticket_uuid),
        new UpdateLocalSeat({
          id: ticket.seat_id,
          ticket_locked: false,
          ticket_locked_by_operator_id: null
        })
      ]);

      return this.ticketService.unlockTicket(device_ticket_uuid);
    }
  }

  @Action(LockOrUnlockTicketSuccess)
  lockOrUnlockTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    {
      ticketUuid,
      locked,
      locked_by_operator_id,
      wsNotified
    }: LockOrUnlockTicketSuccess
  ) {
    ctx.setState(
      produce((draft: TicketsStateModel) => {
        if (draft.tickets.byId && draft.tickets.byId[ticketUuid]) {
          draft.tickets.byId[ticketUuid].locked = locked;
          draft.tickets.byId[ticketUuid].locked_by_operator_id =
            locked_by_operator_id;
        } else {
          draft.tickets.byId = {
            ...draft.tickets.byId,
            [ticketUuid]: <NormalizedTicket>{ locked, locked_by_operator_id }
          };
        }
      })
    );

    if (!locked && wsNotified) {
      return ctx.dispatch(new LoadTicketDetail(ticketUuid));
    }
  }

  @Action(AddTaxExemption)
  addTaxExemption(
    { dispatch }: StateContext<TicketsStateModel>,
    { deviceTicketUuid }: AddTaxExemption
  ) {
    return this.ticketService
      .addTaxExemption(deviceTicketUuid)
      .pipe(tap((ticket) => dispatch(new AddTaxExemptionSuccess(ticket))));
  }

  @Action([AddTaxExemptionSuccess, RemoveTaxExemptionSuccess])
  updateTicketInState(
    { getState, dispatch }: StateContext<TicketsStateModel>,
    { ticket }: AddTaxExemptionSuccess | RemoveTaxExemptionSuccess
  ) {
    if (getState().tickets.allIds.includes(ticket.device_ticket_uuid)) {
      dispatch(new UpdateTicketSuccess(ticket));
    }
  }

  @Action(RemoveTaxExemption)
  removeTaxExemption(
    { dispatch }: StateContext<TicketsStateModel>,
    { prevTicket }: RemoveTaxExemption
  ) {
    if (prevTicket.tax_exempt) {
      return this.ticketService
        .removeTaxExemption(prevTicket.device_ticket_uuid)
        .pipe(tap((ticket) => dispatch(new RemoveTaxExemptionSuccess(ticket))));
    }
  }

  @Action([ChangeTicketMeal, TemporarySeatMealChange])
  setTicketIsChangingMeal({ setState }: StateContext<TicketsStateModel>) {
    this._setTicketProcessing(setState, true);
  }

  @Action([ChangeTicketMealSuccess, TemporarySeatMealChangeSuccess])
  setTicketChangeMealSuccess({ setState }: StateContext<TicketsStateModel>) {
    this._setTicketProcessing(setState, false);
  }

  //1: Cancel or Void Ticket based on status of old Ticket
  //2: Re-select seat and Ticket, currently UI does not go back to seat after removing original Ticket
  //3: Re-add meal plan if it was present on the old Ticket
  //4: Validate QS, no need to re-select seat on there
  @Action(ChangeTicketMeal)
  changeTicketMeal(
    { getState, setState, dispatch }: StateContext<TicketsStateModel>,
    payload: ChangeTicketMeal
  ) {
    const state = getState();
    const prevTicketUuid = state.selectedTicket.data;
    const {
      id: previousTicketId,
      seat_id,
      table_id,
      delivery_note,
      delivery_type,
      diner_id,
      guest_of_diner_id,
      meal_plan
    } = state.tickets.byId[prevTicketUuid];
    const ticketDinerId = diner_id || guest_of_diner_id;
    const meal_id = payload.meal;

    if (!previousTicketId) {
      // If previous ticket is a draft ticket, no need to proceed
      this._setTicketProcessing(setState, false);
      return null;
    }

    const changeTicketMealApiActions$ = this.ticketService
      .deleteTicket(prevTicketUuid)
      .pipe(
        switchMap(() =>
          this.ticketService.createTicket({
            meal_id,
            seat_id,
            table_id,
            delivery_note,
            delivery_type
          })
        )
      );
    const assignDinerToTicket = (ticket: Ticket) =>
      diner_id
        ? this.ticketService
            .assignDinerToTicket(ticket.device_ticket_uuid, diner_id)
            .pipe(
              map(() => ({
                ...ticket,
                diner_id
              }))
            )
        : this.ticketService.updateTicket(ticket.device_ticket_uuid, {
            guest_of_diner_id
          });

    const typeFn: UnaryFunction<
      Observable<Ticket>,
      Observable<Ticket>
    > = ticketDinerId
      ? pipe(
          switchMap((res) => assignDinerToTicket(res)),
          switchMap((res) =>
            meal_plan
              ? this._assignMealPlanToTicket(
                  res.device_ticket_uuid,
                  meal_plan.id
                )
              : of(res)
          ),
          tap((result) => dispatch(new ChangeTicketMealSuccess(result)))
        )
      : pipe(tap((result) => dispatch(new ChangeTicketMealSuccess(result))));

    return changeTicketMealApiActions$.pipe(typeFn);
  }

  @Action(ChangeTicketMealSuccess)
  changeTicketMealSuccess(
    { getState, setState }: StateContext<TicketsStateModel>,
    { newTicket }: ChangeTicketMealSuccess
  ) {
    if (!newTicket) {
      return;
    }

    const state = getState();
    const prevTicketUuid = state.selectedTicket.data;
    const prevTicket = state.tickets.byId[prevTicketUuid];

    setState(
      produce((draft) => {
        const entities = NormalizrHelper.normalizeTickets([newTicket]).entities;

        const operatorId =
          this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;

        delete draft.tickets.byId[draft.selectedTicket.data];
        _.remove(draft.tickets.allIds, draft.selectedTicket.data);
        draft.selectedTicket.data = newTicket.device_ticket_uuid;
        draft.tickets.allIds.push(newTicket.device_ticket_uuid);
        draft.tickets.byId = {
          ...draft.tickets.byId,
          ...entities.tickets
        };
        const draftTicketId = draft.selectedTicket.data;
        draft.tickets.byId[draftTicketId] = {
          ...draft.tickets.byId[draftTicketId],
          locked: true,
          locked_by_operator_id: operatorId
        };

        if (prevTicket.diner_id) {
          draft.tickets.byId[draftTicketId].diner = prevTicket.diner;
          draft.tickets.byId[draftTicketId].diner_id = prevTicket.diner_id;
          draft.tickets.byId[draftTicketId].diner_name = prevTicket.diner_name;
        }
      })
    );
  }

  @Action(SetupTakeoutOrder)
  setupTakeoutOrder(
    ctx: StateContext<TicketsStateModel>,
    { takeoutOrderSetup }: SetupTakeoutOrder
  ) {
    const locationType =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isQuickService: boolean = locationType === LocationType.QuickService;

    if (!isDiningRoom && !isQuickService) {
      ctx.dispatch(
        new SetupTakeoutOrderError(
          this.translocoService.translate('messages.locationNotSupportTakeout')
        )
      );
      return;
    }

    const { tickets, selectedTicket: selectedTicketUuidState } = ctx.getState();
    const selectedTicket =
      tickets.byId && tickets.byId[selectedTicketUuidState.data];

    //Setup for update ticket
    const takeoutOrderTicket: Partial<Ticket> = {
      ticket_date: takeoutOrderSetup.date,
      order_source: TicketSource.POS_TAKEOUT,
      delivery_type: takeoutOrderSetup.orderType,
      meal_id: takeoutOrderSetup.mealId
    };
    const deliveryAreaTiming = takeoutOrderSetup.mealDeliveryAreaTiming;
    if (deliveryAreaTiming) {
      // ASAP tickets have a undefined meal_delivery_area_timing_id
      takeoutOrderTicket.meal_delivery_area_timing_id =
        deliveryAreaTiming.toString().trim() === 'ASAP'
          ? undefined
          : deliveryAreaTiming;
    }

    if (!selectedTicket?.status) {
      ctx
        .dispatch(new CreateTicket(takeoutOrderTicket))
        .subscribe(() => ctx.dispatch(new SetupTakeoutOrderSuccess()));
    } else {
      ctx.dispatch(new CancelTicket()).subscribe(() =>
        ctx
          .dispatch(
            new CreateTicket(takeoutOrderTicket, {
              diner_id: selectedTicket.diner_id,
              mealPlanId: selectedTicket.meal_plan?.id,
              guest_of_diner_id: selectedTicket.guest_of_diner_id
            })
          )
          .subscribe(() => ctx.dispatch(new SetupTakeoutOrderSuccess()))
      );
    }
  }

  // @Action(CreateDraftTicket)
  // createDraftTicket(ctx: StateContext<TicketsStateModel>) {
  //   const draftTicket = TicketHelper.initializeTicket({
  //     device_ticket_uuid:
  //       this.ticketService.generateTicketOrTicketItemUuid('TICKET')
  //   });
  //   const entities = NormalizrHelper.normalizeTickets([draftTicket]).entities;
  //   this.addTicketToState(ctx, draftTicket, true, entities);
  // }

  @Action(RemoveAllSskTicketItems)
  removeAllSskTicketItems(
    ctx: StateContext<TicketsStateModel>,
    { ticketItems }: RemoveAllSskTicketItems
  ) {
    const removedTicketItemUuids = _.flatMapDeep(
      ticketItems.map((ti) => ti.items.map((i) => i.device_ticket_item_uuid))
    );

    ctx.setState(
      produce((draft) => {
        draft.tickets.byId[draft.selectedTicket.data].ticket_items = [];
      })
    );

    return forkJoin(
      removedTicketItemUuids.map((uuid) =>
        this.ticketService.cancelTicketItem(uuid)
      )
    ).pipe(
      tap((ticket) =>
        ctx.dispatch(
          new UpdateTicketSuccess(
            _.minBy(ticket, ({ outstanding_balance }) => +outstanding_balance)
          )
        )
      ),
      catchError((error) =>
        ctx.dispatch(new RemoveAllSskTicketItemsError(error))
      )
    );
  }

  @Action(SelectNewSeat)
  selectNewSeat(ctx: StateContext<TicketsStateModel>, payload: SelectNewSeat) {
    const operatorId = this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;
    const seatState = this.store.selectSnapshot(SEATS_STATE_TOKEN);
    const selectedSeat = seatState.seats.byId[payload.seatId];
    const prevSeatId = seatState.selectedSeat;

    if (prevSeatId) {
      const prevSeat = seatState.seats.byId[prevSeatId];
      const ticketsState = this.store.selectSnapshot(TICKETS_STATE_TOKEN);
      const ticketsById = ticketsState.tickets.byId;
      const prevTicket =
        ticketsById && ticketsById[prevSeat.device_ticket_uuid];

      if (prevTicket) {
        ctx.setState(
          produce((draft: TicketsStateModel) => {
            draft.tickets.byId[prevSeat.device_ticket_uuid].locked = false;
            draft.tickets.byId[
              prevSeat.device_ticket_uuid
            ].locked_by_operator_id = null;
          })
        );
      }
    }

    if (selectedSeat.device_ticket_uuid) {
      ctx.setState(
        produce((draft: TicketsStateModel) => {
          const deviceTicketUuid = selectedSeat.device_ticket_uuid,
            draftTicket =
              draft.tickets.byId && draft.tickets.byId[deviceTicketUuid];

          draft.selectedTicket.data = deviceTicketUuid;

          if (draftTicket) {
            draftTicket.locked = true;
            draftTicket.locked_by_operator_id = operatorId;
          }
        })
      );

      const currentState = ctx.getState();
      const selectedTicket =
        currentState.tickets.byId[selectedSeat.device_ticket_uuid];
      const ticketMealId = selectedTicket?.meal_id;
      ctx.dispatch(
        new UpdateSelectedMealId(ticketMealId, selectedTicket?.ticket_date)
      );
    } else {
      ctx.patchState({});
      return this.ticketService.createTicket({ seat_id: payload.seatId }).pipe(
        filter((ticket) => !!ticket),
        tap((ticket) =>
          ctx.dispatch(
            _.compact([
              new CreateTicketForSeatSuccess(ticket),
              new UpdateSelectedMealId(ticket.meal_id, ticket.ticket_date)
            ])
          )
        )
      );
    }
  }

  @Action(CreateTicketForSeatSuccess)
  createTicketForSeatSuccess(
    ctx: StateContext<TicketsStateModel>,
    payload: CreateTicketForSeatSuccess
  ) {
    const operatorId = this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;

    ctx.setState(
      produce((draft: TicketsStateModel) => {
        // selectedTicket: payload.ticket.device_ticket_uuid
        draft.selectedTicket.data = payload.ticket.device_ticket_uuid;
        const ticketEntities = NormalizrHelper.normalizeTickets([
          payload.ticket
        ]).entities;
        draft.tickets = {
          allIds: _.union(draft.tickets.allIds, [
            payload.ticket.device_ticket_uuid
          ]),
          byId: { ...draft.tickets.byId, ...ticketEntities.tickets }
        };
        draft.tickets.byId[draft.selectedTicket.data].locked = true;
        draft.tickets.byId[draft.selectedTicket.data].locked_by_operator_id =
          operatorId;
      })
    );

    return merge([
      this.ticketService.lockTicket(
        payload.ticket.device_ticket_uuid,
        operatorId
      ),
      ctx.dispatch(new SetSelectedMealId(payload.ticket.meal_id))
    ]);
  }

  @Action(UnselectSeat)
  unselectSeat(ctx: StateContext<TicketsStateModel>) {
    const { selectedTicket, tickets } = ctx.getState();
    const ticketUuid = selectedTicket.data;
    const ticketsById = tickets.byId;
    const unlockedTicket = ticketsById && ticketsById[ticketUuid];

    ctx.setState(
      patch({
        selectedTicket: {
          data: null,
          isProcessing: false,
          isLoaded: false
        }
      })
    );

    if (unlockedTicket && unlockedTicket.locked) {
      ctx.setState(
        produce((draft: TicketsStateModel) => {
          draft.tickets.byId[ticketUuid].locked = false;
          draft.tickets.byId[ticketUuid].locked_by_operator_id = null;
        })
      );

      return this.ticketService.unlockTicket(ticketUuid);
    }
  }

  @Action(TicketClosedWsEvent)
  ticketClosedWsEvent(
    ctx: StateContext<TicketsStateModel>,
    { wsData }: TicketClosedWsEvent
  ) {
    const { selectedTicket, tickets } = ctx.getState();
    const selectedTicketId = selectedTicket.data;
    const device_ticket_uuid = wsData.device_ticket_uuid;
    if (tickets.allIds.includes(device_ticket_uuid)) {
      if (selectedTicketId === device_ticket_uuid) {
        this.removeTicketSuccessHandler(ctx, {
          device_ticket_uuid,
          removeTicket: true
        });
      } else {
        ctx.setState(
          produce((draft) => {
            draft.tickets.byId = _.omit(draft.tickets.byId, device_ticket_uuid);
            draft.tickets.allIds = Object.keys(draft.tickets.byId);
          })
        );
      }
    }
  }

  private removeTicketSuccessHandler(
    ctx: StateContext<TicketsStateModel>,
    payload: { device_ticket_uuid: string; removeTicket: boolean }
  ) {
    const { device_ticket_uuid, removeTicket } = payload;
    const state = ctx.getState();
    const ticket = state.tickets.byId && state.tickets.byId[device_ticket_uuid];
    if (!ticket) {
      return;
    }

    if (
      ticket.status !== TicketStatus.CLOSED &&
      ticket.takeout_order_status &&
      ticket.locked
    ) {
      this.ticketService.unlockTicket(device_ticket_uuid).subscribe();
    }

    const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
    const locationType = location.type;
    const isDiningRoom: boolean = locationType === LocationType.DiningRoom;
    const isQuickService: boolean = locationType === LocationType.QuickService;
    const isTakeoutDelivery: boolean = ticket.order_source !== TicketSource.POS;
    let isRemoved = false;

    const removeTicketAction = () => {
      ctx.setState(
        produce((draft) => {
          //Remove ticket if it exists locally - Ticket will not exist locally if one user is on the floor plan and the other user is working on a table
          if (_.has(draft.tickets.byId, device_ticket_uuid)) {
            isRemoved = true;
            delete draft.tickets.byId[device_ticket_uuid];
            _.remove(
              draft.tickets.allIds,
              (item) => item === device_ticket_uuid
            );
          }

          if (!device_ticket_uuid) {
            draft.selectedTicket = {
              data: null,
              isProcessing: false,
              isLoaded: false
            };
          }
        })
      );

      if (isDiningRoom && !isTakeoutDelivery) {
        ctx.dispatch(new SelectSeat(ticket?.seat_id, true));
      }
    };

    //Unselect the seat if in dining room
    if (isDiningRoom) {
      if (isTakeoutDelivery) {
        removeTicketAction();
        ctx.dispatch(new SelectTicket());
      } else {
        const seatId = ticket?.seat_id;
        const table_id = ticket?.table_id;
        const localSeat = {
          id: seatId,
          table_id,
          device_ticket_uuid: undefined,
          floorplan_status: undefined,
          ticket: undefined,
          ticket_created_at: undefined,
          ticket_status: undefined,
          ticket_locked: false,
          ticket_locked_by_operator_id: undefined,
          diner_id: undefined
        };

        ctx.dispatch(new UpdateLocalSeat(localSeat));
        const isClosedTicket = ticket?.status === TicketStatus.CLOSED;

        if (isClosedTicket || removeTicket) {
          removeTicketAction();
          if (!isRemoved) {
            return;
          }
          ctx.dispatch(new SelectSeat());
          return;
        }
      }
    } else if (isQuickService) {
      removeTicketAction();
      const isQuickServicePage = this.router.url.includes(
        PageConstant.QUICK_SERVICE_PAGE
      );
      const isTakeOutDeliveryPage = this.router.url.includes(
        PageConstant.TAKEOUT_DELIVERY_PAGE(isDiningRoom)
      );

      ctx.dispatch(
        new StateReset(DinersState, TicketItemsState, TransactionsState)
      );

      const actions = [];

      if (isQuickServicePage) {
        const newTicket = TicketHelper.initializeTicket({
          device_ticket_uuid:
            this.ticketService.generateTicketOrTicketItemUuid('TICKET')
        });
        actions.push(new GetLastQuickServiceTicketSuccess(newTicket, true));
        // this.navController.back();
      } else {
        actions.push(
          //DEF-4575: Ensure that the header displays the correct buttons and the ticket is reset
          isTakeOutDeliveryPage ? new ExitTakeOutDelivery() : new SelectTicket()
        );
      }
      return ctx.dispatch(actions);
    }

    return ctx.dispatch(
      new StateReset(
        DinersState,
        TicketItemsState,
        TransactionsState,
        TicketsState
      )
    );
  }

  @Action(TemporarySeatMealChange)
  async temporarySeatMealChange(
    { getState, dispatch }: StateContext<TicketsStateModel>,
    { meal_id }: TemporarySeatMealChange
  ) {
    // const state = ctx.getState();
    const { selectedTicket, tickets } = getState();
    const selectedTicketId = selectedTicket.data;
    const {
      diner_id,
      guest_of_diner_id,
      meal_plan,
      table_id,
      delivery_note,
      delivery_type,
      seat_id
    } = tickets.byId[selectedTicketId];
    const assignedMealPlanId = meal_plan?.id;
    const assignDinerToTicket = (device_ticket_uuid: string) =>
      diner_id
        ? this.ticketService.assignDinerToTicket(device_ticket_uuid, diner_id)
        : this.ticketService.updateTicket(device_ticket_uuid, {
            guest_of_diner_id
          });
    await lastValueFrom(this.ticketService.deleteTicket(selectedTicketId));
    const newSeat = await lastValueFrom(
      this.diningService.createTemporarySeat(table_id)
    );
    let ticket = await lastValueFrom(
      this.ticketService.createTicket({
        meal_id,
        table_id,
        delivery_note,
        delivery_type,
        seat_id: newSeat.id
      })
    );

    const newTicketUuid = ticket.device_ticket_uuid;

    if (diner_id || guest_of_diner_id) {
      ticket = await lastValueFrom(assignDinerToTicket(newTicketUuid));

      if (assignedMealPlanId) {
        ticket = await lastValueFrom(
          this._assignMealPlanToTicket(newTicketUuid, assignedMealPlanId)
        );
      }
    }

    dispatch(
      new TemporarySeatMealChangeSuccess(
        seat_id,
        selectedTicketId,
        newSeat,
        ticket
      )
    );
  }

  @Action(TemporarySeatMealChangeSuccess)
  temporarySeatMealChangeSuccess(
    { setState }: StateContext<TicketsStateModel>,
    { prevTicketUuid, ticket }: TemporarySeatMealChangeSuccess
  ) {
    setState(
      produce((draft) => {
        const ticketEntities = NormalizrHelper.normalizeTickets([ticket])
          .entities.tickets;
        delete draft.tickets.byId[prevTicketUuid];
        draft.tickets.allIds = _.uniq(
          draft.tickets.allIds
            .filter((id) => id !== prevTicketUuid)
            .concat(ticket.device_ticket_uuid)
        );
        draft.tickets.byId = {
          ...draft.tickets.byId,
          ...ticketEntities
        };
        draft.selectedTicket.data = ticket.device_ticket_uuid;
        draft.tickets.byId[draft.selectedTicket.data] = {
          ...draft.tickets.byId[draft.selectedTicket.data],
          diner_id: ticket.diner_id,
          meal_plan: ticket.meal_plan,
          meal_plan_id: ticket.meal_plan?.id
        };
      })
    );
  }

  @Action(TicketLockedChangedFromWS)
  ticketLockedChangedFromWS(
    ctx: StateContext<TicketsStateModel>,
    payload: TicketLockedChangedFromWS
  ) {
    const selectedSeatId =
      this.store.selectSnapshot(SEATS_STATE_TOKEN).selectedSeat;
    const isCurrentSeatChanged =
      !!selectedSeatId && selectedSeatId === payload.data.seat_id;
    const operatorId = this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;

    if (!isCurrentSeatChanged) {
      return ctx.dispatch(
        new LockOrUnlockTicketSuccess(
          payload.data.device_ticket_uuid,
          payload.data.locked,
          payload.data.locked_by_operator_id,
          true
        )
      );
    }

    if (isCurrentSeatChanged) {
      return this.ticketService.lockTicket(
        payload.data.device_ticket_uuid,
        operatorId
      );
    }
  }

  @Action(CancelServicesTicket)
  cancelServicesTicket(
    ctx: StateContext<TicketsStateModel>,
    payload: CancelServicesTicket
  ) {
    ctx.setState(
      produce((draft: TicketsStateModel) => {
        const canceledTicket = draft.tickets.byId[payload.device_ticket_uuid];
        if (canceledTicket) {
          delete draft.tickets.byId[payload.device_ticket_uuid];
          _.remove(draft.tickets.allIds, payload.device_ticket_uuid);
          draft.selectedTicket.data = null;
        }
      })
    );
  }

  @Action(SetTicketCreatingStatus)
  setTicketCreatingStatus(
    { setState }: StateContext<TicketsStateModel>,
    { isCreating }: SetTicketCreatingStatus
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicket.isProcessing = isCreating;
      })
    );
  }

  @Action(FireTicketAndClose)
  fireTicketAndClose(
    { getState, setState, dispatch }: StateContext<TicketsStateModel>,
    { deviceTicketUuid, paymentTypes }: FireTicketAndClose
  ) {
    const selectedTicketState = getState().selectedTicket,
      isSelectedTicketValid = selectedTicketState.data === deviceTicketUuid,
      isSelectedTicketProcessing = selectedTicketState.isProcessing;

    if (isSelectedTicketProcessing) {
      return;
    }

    if (isSelectedTicketValid) {
      this._setTicketProcessing(setState, true);
    }

    //Action will only fire a ticket, used in Self Service
    return this.ticketService
      .fireTicket(
        deviceTicketUuid,
        this.store.selectSnapshot(APP_STATE_TOKEN).operator.id
      )
      .pipe(
        switchMap(() =>
          dispatch(new CloseTicket(deviceTicketUuid, paymentTypes))
        ),
        catchError(() =>
          dispatch(
            new FireTicketAndCloseError(
              this.translocoService.translate('messages.problemFiringOrder')
            )
          )
        )
      );
  }

  @Action(FireTicketAndCloseError)
  fireTicketAndCloseError({ setState }: StateContext<TicketsStateModel>) {
    // this.modalService.closeLoadingModal();
    this._setTicketProcessing(setState, false);
  }

  @Action(AssignGuestMealPlan)
  assignGuestMealPlan(
    ctx: StateContext<TicketsStateModel>,
    { diner_id, device_ticket_uuid }: AssignGuestMealPlan
  ) {
    return this.ticketService
      .assignGuestMealPlan(device_ticket_uuid, diner_id)
      .pipe(tap((ticket) => TicketsState.updateTicketSuccess(ctx, ticket)));
  }

  @Action(CreateTicketSuccess)
  addFeeToTakeoutTicket(
    ctx: StateContext<TicketsStateModel>,
    { ticket }: CreateTicketSuccess
  ) {
    if (ticket.order_source === TicketSource.POS_TAKEOUT) {
      const location = this.store.selectSnapshot(LOCATION_STATE_TOKEN).location;
      const pickupDeliveryFee = TicketHelper.getPickupDeliveryFee(
        ticket.delivery_type,
        location.pickup_delivery_fees
      );

      if (pickupDeliveryFee) {
        ctx.dispatch(new AddPickupDeliveryFeeToTicket(pickupDeliveryFee));
      }
    }
  }

  @Action(FireTicketSuccess)
  fireTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    { deviceTicketUuid }: FireTicketSuccess
  ) {
    ctx.setState(
      produce((draft) => {
        draft.tickets.byId[deviceTicketUuid] = {
          ...draft.tickets.byId[deviceTicketUuid],
          can_be_cancelled: false,
          status: TicketStatus.FIRED
        };
      })
    );
  }

  @Action(UnlockTakeoutTicket)
  unlockTakeoutTicket(
    { getState, setState }: StateContext<TicketsStateModel>,
    { ticketId }: UnlockTakeoutTicket
  ) {
    const { selectedTicket, tickets } = getState();
    const includeTicket = tickets.allIds.includes(ticketId);

    if (includeTicket) {
      setState(
        produce((draft) => {
          if (selectedTicket.data === ticketId) {
            draft.selectedTicket.data = null;
          }

          draft.tickets.byId[ticketId].locked = false;
          draft.tickets.byId[ticketId].locked_by_operator_id = null;
        })
      );

      return this.ticketService.unlockTicket(ticketId);
    }
  }

  private getWaitingTicketItemUuidArray(ticketItemUuids: string[]): string[] {
    const ticketItems = this.getTicketItemsByUuids(ticketItemUuids);

    return ticketItems.reduce((acc, item) => {
      if (item.status === TicketItemStatus.WAITING) {
        acc.push(item.device_ticket_item_uuid);
      }
      return acc;
    }, [] as string[]);
  }

  private getTicketItemsByUuids(
    ticketItemUuids: string[]
  ): NormalizedTicketItem[] {
    return ticketItemUuids.reduce((acc, uuid) => {
      const item = this.store.selectSnapshot(TICKET_ITEMS_STATE_TOKEN)
        .ticketItems.byId[uuid];

      if (item) {
        acc.push(item);
      }

      return acc;
    }, [] as NormalizedTicketItem[]);
  }

  private static doPrintTicket(
    {
      type,
      print_receipt_for_non_cash,
      print_receipt_for_cash_payment,
      auto_close_on_full_payment
    }: PosLocation,
    { outstanding_balance, order_source }: NormalizedTicket,
    paymentRequestTypes: PaymentRequestType[]
  ): boolean {
    if (type === LocationType.SelfServe) {
      return print_receipt_for_non_cash;
    }

    // DEF-5628: Ticket has been printed on payment success, so no need to print again
    if (order_source !== TicketSource.POS && auto_close_on_full_payment) {
      return false;
    }

    const paymentTypes = paymentRequestTypes || [],
      isCashPayment = paymentTypes.includes(PaymentRequestType.CASH),
      hasPaymentOrZeroBalance = paymentTypes.length || !outstanding_balance;

    return isCashPayment
      ? print_receipt_for_cash_payment
      : hasPaymentOrZeroBalance && print_receipt_for_non_cash;
  }

  private static updateTicketSuccess(
    ctx: StateContext<TicketsStateModel>,
    data: Partial<Ticket>
  ) {
    const normalizeTickets = NormalizrHelper.normalizeTickets([<Ticket>data])
      .entities.tickets;

    ctx.setState(
      produce((draft) => {
        const ticketUuid = data.device_ticket_uuid || draft.selectedTicket.data;
        if (normalizeTickets) {
          draft.tickets.byId[ticketUuid] = {
            ...draft.tickets.byId[ticketUuid],
            ...normalizeTickets[ticketUuid]
          };
        }
      })
    );
  }

  private formatDate(date: number | Date, formatString: string = 'yyyy-MM-dd') {
    return format(date, formatString);
  }

  private addTicketItemLocalAttributes(
    ticketItem: TicketItem,
    ticketItems: TicketItem[]
  ): TicketItem {
    ticketItem.local_attributes = {
      removed: false
    };
    if (!ticketItem?.parent_uuid) {
      ticketItem.ticket_items = ticketItems.filter(
        (i) => i.parent_uuid === ticketItem.device_ticket_item_uuid
      );
    }
    return ticketItem;
  }

  private selectDiner(payload: {
    ctx: StateContext<TicketsStateModel>;
    ticketUuid: string;
    addMealPlan?: boolean;
    forceApplyMealPlan?: boolean;
  }) {
    const { ctx, addMealPlan, forceApplyMealPlan, ticketUuid } = payload;
    const { byId } = ctx.getState().tickets;
    const ticket = byId[ticketUuid];

    if (ticket) {
      const allDinersIds =
        this.store.selectSnapshot(DINERS_STATE_TOKEN).diners.allIds;
      const getDinerDetail = !allDinersIds.includes(ticket.diner_id);

      ctx.dispatch(
        new SelectDiner(ticket, addMealPlan, getDinerDetail, forceApplyMealPlan)
      );
    }
  }

  private updateDinerToLocalSeat(
    ctx: StateContext<TicketsStateModel>,
    ticketUuid: string
  ) {
    const state = ctx.getState();
    if (state.tickets.allIds.indexOf(ticketUuid) !== -1) {
      const ticket = state.tickets.byId[ticketUuid];
      const diner = ticket?.diner_id || null;
      ctx.dispatch(
        new UpdateLocalSeat({
          id: ticket?.seat_id,
          diner_id: diner,
          tableId: ticket.table_id
        })
      );
    }
  }

  private updateMenuForTicket(
    ctx: StateContext<TicketsStateModel>,
    date: string,
    ticketId: string,
    actions?: any[]
  ) {
    const state = ctx.getState();
    const menuSelectedMealId =
      this.store.selectSnapshot(MENU_STATE_TOKEN).selected_meal_id;
    const nextTicket = state.tickets.byId && state.tickets.byId[ticketId];
    const ticketMealId = nextTicket?.meal_id;
    if (!ticketMealId || ticketMealId === menuSelectedMealId) {
      return;
    }

    if (actions) {
      actions.push(new UpdateSelectedMealId(nextTicket.meal_id, date));
      return;
    }

    return ctx.dispatch(new UpdateSelectedMealId(nextTicket.meal_id, date));
  }

  private addTicketToState(
    { setState }: StateContext<TicketsStateModel>,
    ticket: Ticket,
    selectTicket = false,
    entities: Pick<
      NormalizedEntities,
      'tickets' | 'ticket_items' | 'transactions' | 'diners'
    >
  ) {
    setState(
      produce((draft) => {
        if (entities.tickets) {
          if (draft.tickets.allIds.includes(ticket.device_ticket_uuid)) {
            draft.tickets.byId[ticket.device_ticket_uuid] = {
              ...draft.tickets.byId[ticket.device_ticket_uuid],
              ...entities.tickets[ticket.device_ticket_uuid]
            };
          } else {
            draft.tickets.allIds.push(ticket.device_ticket_uuid);
            draft.tickets.byId[ticket.device_ticket_uuid] =
              entities.tickets[ticket.device_ticket_uuid];
          }
        }
        if (selectTicket) {
          draft.selectedTicket.data = ticket.device_ticket_uuid;
        }
      })
    );
  }

  private _setTicketProcessing(
    setState: (
      val: TicketsStateModel | StateOperator<TicketsStateModel>
    ) => TicketsStateModel,
    isProcessing: boolean
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicket.isProcessing = isProcessing;
      })
    );
  }

  /**
   * Assigns a meal plan to a ticket and shows a loading modal during the operation.
   *
   * @param ticket_uuid The UUID of the ticket to which the meal plan will be assigned.
   * @param pos_mealplan_id The ID of the POS meal plan to assign.
   * @returns An Observable that emits the updated Ticket.
   */
  private _assignMealPlanToTicket(
    ticket_uuid: string,
    pos_mealplan_id: number
  ): Observable<Ticket> {
    return from(
      this.modalService.showLoadingModal(
        this.translocoService.translate('messages.assigningMealPlan')
      )
    ).pipe(
      switchMap((loadingModal) =>
        this.ticketService.addMealPlan(ticket_uuid, pos_mealplan_id).pipe(
          tap(() => this.modalService.closeLoadingModal(loadingModal.id)),
          catchError((error: HttpErrorResponse) => {
            this.modalService.closeLoadingModal(loadingModal.id);
            this.modalService.alertErrorMessage(
              error.error.message || 'Unable to assign the meal plan.'
            );
            return EMPTY;
          })
        )
      )
    );
  }

  @Action(UnselectTicket)
  unselectTicket({
    patchState,
    getState,
    dispatch
  }: StateContext<TicketsStateModel>) {
    const { selectedTicket, tickets } = getState();
    const ticket = tickets.byId && tickets.byId[selectedTicket.data];

    if (ticket.locked) {
      dispatch(new UnlockTicket(ticket.device_ticket_uuid));
    }

    patchState({
      selectedTicket: {
        data: null,
        isProcessing: false,
        isLoaded: false
      }
    });
  }

  @Action(LockTakeoutTicket)
  lockTakeoutTicket(
    { setState }: StateContext<TicketsStateModel>,
    { ticketId }: LockTakeoutTicket
  ) {
    const operator = this.store.selectSnapshot(APP_STATE_TOKEN).operator;
    setState(
      produce((draft) => {
        draft.tickets.byId[ticketId].locked = true;
        draft.tickets.byId[ticketId].locked_by_operator_id = operator.id;
      })
    );
    return this.ticketService.lockTicket(ticketId, operator.id);
  }

  @Action(CancelOrder)
  cancelOrder({ getState, dispatch }: StateContext<TicketsStateModel>) {
    return this.ticketService.deleteTicket(getState().selectedTicket.data).pipe(
      tap(() => {
        this.store.dispatch(new StopIdle());
        this.sskProvider.resetSelfService();
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          dispatch(new CancelOrderError(error));
        }

        return EMPTY;
      })
    );
  }

  @Action(SetTicketProcessing)
  /**
   * Sets the ticket processing state.
   * @param {StateContext<TicketsStateModel>} context - The state context for tickets.
   * @param {SetTicketProcessing} payload - Object containing the processing flag.
   * @returns {void}
   */
  setTicketProcessing(
    { setState }: StateContext<TicketsStateModel>,
    { isProcessing }: SetTicketProcessing
  ) {
    this._setTicketProcessing(setState, isProcessing);
  }
}
